import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import 'react-image-crop/dist/ReactCrop.css';
import { GripHorizontal, WifiOff, Undo2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

import { normalizePlantData, findNameOnlyMatch } from './add-plant-steps/plantUtils';
import { BatchProgress } from './add-plant-steps/BatchProgress';
import { UploadStep } from './add-plant-steps/UploadStep';
import { CropStep } from './add-plant-steps/CropStep';
import { IdentifiedStep } from './add-plant-steps/IdentifiedStep';
import { DetailsStep } from './add-plant-steps/DetailsStep';
import { SummaryStep } from './add-plant-steps/SummaryStep';
import { DuplicateDialog } from './add-plant-steps/DuplicateDialog';
import { ConsolidateDialog } from './add-plant-steps/ConsolidateDialog';
import * as localStore from '../services/localPlantStore';
import { useLanguage } from '../contexts/LanguageContext';
import { readExifOrientation, orientationToRotation } from '../utils/exifOrientation';
import { withRetry } from '../utils/retryUpload';
import { API_BASE_URL } from '../utils/apiBase';
const BACKEND_URL = API_BASE_URL;

export const AddPlantModal = ({ onClose, onPlantAdded, onEditPlant = null, editPlant = null, existingPlants = [], onDelete, isLocalMode = false, initialFile = null, pendingWorkRef = null, initialDraft = null }) => {
  const { t } = useLanguage();
  const [step, setStep] = useState(editPlant ? 'details' : 'upload');
  // Live offline flag so the add flow can warn upfront that a brand-new
  // plant needs a connection (edits/archives/deletes queue offline, adds don't).
  const [isOffline, setIsOffline] = useState(typeof navigator !== 'undefined' && navigator.onLine === false);
  useEffect(() => {
    const on = () => setIsOffline(false);
    const off = () => setIsOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(editPlant?.photo_drive_id ? 'existing' : null);
  const [crop, setCrop] = useState({ unit: '%', width: 90 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [croppedPreview, setCroppedPreview] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [identifying, setIdentifying] = useState(false);
  const [saving, setSaving] = useState(false);

  // Batch upload state
  const [fileQueue, setFileQueue] = useState([]);
  const [currentQueueIndex, setCurrentQueueIndex] = useState(0);
  const [savedPlants, setSavedPlants] = useState([]);
  const [requeuedFiles, setRequeuedFiles] = useState([]);
  const [isSecondPass, setIsSecondPass] = useState(false);
  const batchMode = fileQueue.length > 1;
  // Branded confirm for permanently dropping ONE photo mid-batch (e.g. a
  // duplicate the user already added) without touching the rest of the queue.
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  // Holds a merge whose upload failed with a TRANSIENT error so the user can
  // retry instead of losing the photo. Permanent rejections don't set this.
  const [failedConsolidate, setFailedConsolidate] = useState(null);
  // Snapshot of batch state captured right before a photo is removed, so the
  // "Undo" action can restore that exact photo.
  const undoRemoveRef = useRef(null);
  // Inline "Photo removed · Undo" notice shown INSIDE the modal. A toast can't
  // be used here because the Radix Dialog's modal overlay blocks pointer
  // events to anything rendered outside the dialog (incl. sonner toasts).
  const [removedNotice, setRemovedNotice] = useState(null);
  const removedTimerRef = useRef(null);
  // Hashes of photos already committed in THIS batch — lets us flag a photo
  // that duplicates one added earlier in the same session (existingPlants is
  // stale mid-batch since the parent list only refreshes at the end).
  const [batchHashes, setBatchHashes] = useState([]);

  // Multi-photo Add: ONE plant built from up to MAX_PHOTOS_PER_PLANT
  // photos. Distinct from `batchMode` (which creates one plant per photo).
  // We walk the user through the crop UI for each file in order, collect
  // the cropped base64 into `multiCroppedPhotos`, then send them all to
  // /identify together so the AI sees the whole plant. Capped at 5.
  const MAX_PHOTOS_PER_PLANT = 5;
  const [multiPhotoMode, setMultiPhotoMode] = useState(false);
  const [multiCropQueue, setMultiCropQueue] = useState([]);
  const [multiCropIndex, setMultiCropIndex] = useState(0);
  const [multiCroppedPhotos, setMultiCroppedPhotos] = useState([]); // list of dataURL strings
  const [multiCroppedHashes, setMultiCroppedHashes] = useState([]);

  const [plantData, setPlantData] = useState({
    common_name: editPlant?.common_name || '',
    scientific_name: editPlant?.scientific_name || '',
    care_details: editPlant?.care_details || {
      watering: '', light: '', humidity: '', temperature: '', toxicity: '', toxicity_notes: '', growth_rate: '', family: '', plant_type: '', environment: '', bloom_season: '', bloom_duration: '', blooms_indoors: '', bloom_tips: ''
    }
  });

  // Keep the parent's pending-work ref in sync so a browser back /
  // swipe-back mid-batch triggers a confirm instead of silently dropping
  // queued photos. `pendingCount` counts photos still to process AFTER
  // the current one — batch mode adds any requeued files, multi-photo
  // mode counts the crop-queue tail.
  useEffect(() => {
    if (!pendingWorkRef) return;
    let remaining = 0;
    if (fileQueue.length > 1) {
      remaining = Math.max(0, fileQueue.length - currentQueueIndex - 1) + requeuedFiles.length;
    } else if (multiPhotoMode) {
      remaining = Math.max(0, multiCropQueue.length - multiCropIndex - 1);
    }
    pendingWorkRef.current = { pendingCount: remaining, isCropping: step === 'crop' };
  }, [pendingWorkRef, step, fileQueue.length, currentQueueIndex, requeuedFiles.length, multiPhotoMode, multiCropQueue.length, multiCropIndex]);
  useEffect(() => {
    return () => { if (pendingWorkRef) pendingWorkRef.current = { pendingCount: 0, isCropping: false }; };
  }, [pendingWorkRef]);

  // Ref used by the effect below (declared here so hoisting works).
  const _draftHydratedRef = useRef(false);

  const imageRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Drag state
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const dragStart = useRef(null);

  const handleDragStart = (e) => {
    if (e.target.closest('button')) return;
    dragStart.current = { x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y };
    document.addEventListener('pointermove', handleDragMove);
    document.addEventListener('pointerup', handleDragEnd);
  };

  const handleDragMove = useCallback((e) => {
    if (!dragStart.current) return;
    setDragOffset({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
  }, []);

  const handleDragEnd = useCallback(() => {
    dragStart.current = null;
    document.removeEventListener('pointermove', handleDragMove);
    document.removeEventListener('pointerup', handleDragEnd);
  }, [handleDragMove]);

  // Additional state for identified step
  const [lookingUp, setLookingUp] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [duplicateMatch, setDuplicateMatch] = useState(null);
  const [pendingSaveIncomplete, setPendingSaveIncomplete] = useState(false);
  const [currentImageHash, setCurrentImageHash] = useState(null);
  const [consolidateMatch, setConsolidateMatch] = useState(null);
  const [consolidatePendingIncomplete, setConsolidatePendingIncomplete] = useState(false);
  // Set when the AI identify call throws — surfaces a "Try AI again"
  // button on the Details step so the user can retry without redoing
  // their crops. Cleared on any successful identify or lookup.
  const [identifyFailed, setIdentifyFailed] = useState(false);

  // --- Helpers ---

  const computeImageHash = async (base64Data) => {
    try {
      const binaryStr = atob(base64Data);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
      const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
      return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
    } catch {
      return null;
    }
  };

  // --- File & Image Helpers ---

  const [cropLoadError, setCropLoadError] = useState(false);
  const retryInputRef = useRef(null);

  // Snapshot a picked File into a fresh in-memory File immediately, while its
  // OS handle is still valid. Mobile browsers frequently NEUTER the File
  // handles from a multi-select once the page does heavy work (e.g. cropping
  // the first photo), so by the time the batch advances to the 2nd item a
  // lazy FileReader read silently yields nothing. Copying the bytes up front
  // makes every queued item self-contained and immune to that staleness.
  const snapshotFile = useCallback(async (file) => {
    try {
      const buf = await file.arrayBuffer();
      return new File([buf], file.name || 'photo.jpg', {
        type: file.type || 'image/jpeg',
        lastModified: file.lastModified || Date.now(),
      });
    } catch {
      return file; // read failed even now — onerror handling below will catch it
    }
  }, []);

  const loadFileForCrop = useCallback((file) => {
    setSelectedFile(file);
    setCropLoadError(false);
    setCrop({ unit: '%', width: 90 });
    setCompletedCrop(null);
    setCroppedPreview(null);
    setRotation(0);
    setIdentifyFailed(false);
    setPlantData({
      common_name: '', scientific_name: '',
      care_details: { watering: '', light: '', humidity: '', temperature: '', toxicity: '', growth_rate: '' }
    });
    setEditedName('');
    // Pre-seed rotation from the file's EXIF Orientation tag so shots
    // already upright at capture don't need a manual Rotate tap. Runs
    // async but the CropStep reads `rotation` reactively so it just
    // snaps into place once the parse completes.
    readExifOrientation(file).then((orient) => {
      setRotation(orientationToRotation(orient));
    }).catch(() => { /* leave rotation at 0 */ });
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
      setStep('crop');
    };
    reader.onerror = reader.onabort = () => {
      // A stale/unreadable handle must NOT silently strand the flow on a blank
      // crop step. Surface an in-place retry panel (see the 'crop' render) so
      // the user can swap in a different photo WITHOUT losing the rest of the
      // batch, instead of dropping the whole queue.
      setImagePreview(null);
      setCropLoadError(true);
      setStep('crop');
    };
    reader.readAsDataURL(file);
  }, [t]);

  // Retry a failed photo read by letting the user pick a replacement for the
  // CURRENT slot. The rest of the batch queue is untouched — after cropping
  // this replacement, advanceQueue moves on to the next queued photo as usual.
  const handleRetryPhoto = async (e) => {
    const f = (e.target.files || [])[0];
    if (e.target) e.target.value = '';
    if (!f) return;
    try {
      const { normalizeImageFile } = await import('../utils/imageFormat');
      const norm = await normalizeImageFile(f);
      const stable = await snapshotFile(norm);
      loadFileForCrop(stable);
    } catch {
      setCropLoadError(true);
    }
  };

  const clearBatchDraft = useCallback(async () => {
    try { const { clearDraft } = await import('../utils/addPlantDraft'); await clearDraft(); }
    catch { /* best-effort */ }
  }, []);

  // Draft auto-save — persist any remaining unprocessed queue to
  // IndexedDB so a refresh, tab-close, or accidental app-kill doesn't
  // vaporise the batch. LibraryPage checks for the draft on mount and
  // surfaces a "Resume N unfinished plants" banner. Successful
  // completion or explicit discard clears the draft.
  useEffect(() => {
    if (editPlant) return;
    let cancelled = false;
    (async () => {
      const { saveDraft, clearDraft } = await import('../utils/addPlantDraft');
      if (cancelled) return;
      // The batch is finished once we reach the summary — clear the draft so
      // the just-completed queue never lingers as a phantom "resume unfinished
      // plants" item (the last processed photo would otherwise stay in the
      // draft because currentQueueIndex still points at it).
      if (step === 'summary') { clearDraft(); return; }
      let remaining = [];
      let mode = null;
      if (fileQueue.length > 1) {
        mode = 'batch';
        remaining = [...fileQueue.slice(currentQueueIndex), ...requeuedFiles];
      } else if (multiPhotoMode && multiCropQueue.length > 0) {
        mode = 'multi-photo';
        remaining = multiCropQueue.slice(multiCropIndex);
      }
      if (remaining.length === 0) {
        clearDraft();
        return;
      }
      saveDraft(mode, remaining, savedPlants.length);
    })();
    return () => { cancelled = true; };
  }, [editPlant, step, fileQueue, currentQueueIndex, requeuedFiles, multiPhotoMode, multiCropQueue, multiCropIndex, savedPlants.length]);

  // Resume from a saved draft when LibraryPage opens the modal with a
  // draft payload. Runs once on mount if `initialDraft` is present.
  useEffect(() => {
    if (_draftHydratedRef.current) return;
    if (!initialDraft || !Array.isArray(initialDraft.files) || initialDraft.files.length === 0) return;
    _draftHydratedRef.current = true;
    const { mode, files } = initialDraft;
    if (mode === 'multi-photo') {
      setMultiPhotoMode(true);
      setMultiCropQueue(files);
      setMultiCropIndex(0);
      setMultiCroppedPhotos([]);
      setMultiCroppedHashes([]);
      loadFileForCrop(files[0]);
    } else {
      setFileQueue(files);
      setCurrentQueueIndex(0);
      setSavedPlants([]);
      setRequeuedFiles([]);
      setIsSecondPass(false);
      loadFileForCrop(files[0]);
    }
  }, [initialDraft, loadFileForCrop]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    // Allow either MIME `image/*` OR an image extension — iOS gallery
    // pickers sometimes report `application/octet-stream` for HEIC.
    const imageFiles = files.filter(f => {
      if ((f.type || '').startsWith('image/')) return true;
      const n = (f.name || '').toLowerCase();
      return /\.(heic|heif|jpe?g|png|webp|gif|bmp)$/.test(n);
    });
    if (imageFiles.length === 0) { toast.error('Please select valid image files'); return; }
    // HEIC → JPEG conversion up-front so the crop preview isn't blank.
    const { normalizeImageFile, isHeic } = await import('../utils/imageFormat');
    const anyHeic = imageFiles.some(isHeic);
    if (anyHeic) toast.info(t('convertingHeic') || 'Converting HEIC…');
    const normalized = [];
    for (const f of imageFiles) {
      normalized.push(await normalizeImageFile(f));
    }
    // Copy bytes into stable in-memory Files now, before any handle can go
    // stale during cropping — this is what fixes the batch "2nd photo won't
    // load / comes back unfinished" bug on mobile.
    const stable = await Promise.all(normalized.map(snapshotFile));

    if (stable.length > 1) {
      setFileQueue(stable);
      setCurrentQueueIndex(0);
      setSavedPlants([]);
      setRequeuedFiles([]);
      setIsSecondPass(false);
      loadFileForCrop(stable[0]);
    } else {
      setFileQueue([]);
      loadFileForCrop(stable[0]);
    }
  };

  // Multi-photo Add — ONE plant built from up to MAX_PHOTOS_PER_PLANT
  // photos. Called by the "Add plant with multiple photos" tile on the
  // Upload step. Silently trims to the 5-photo cap and toasts once so
  // large gallery selections don't error out.
  const handleMultiFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const imageFiles = files.filter(f => {
      if ((f.type || '').startsWith('image/')) return true;
      const n = (f.name || '').toLowerCase();
      return /\.(heic|heif|jpe?g|png|webp|gif|bmp)$/.test(n);
    });
    if (imageFiles.length === 0) { toast.error('Please select valid image files'); return; }
    const { normalizeImageFile, isHeic } = await import('../utils/imageFormat');
    const anyHeic = imageFiles.some(isHeic);
    if (anyHeic) toast.info(t('convertingHeic') || 'Converting HEIC…');
    const normalized = [];
    for (const f of imageFiles) {
      normalized.push(await normalizeImageFile(f));
    }
    const stable = await Promise.all(normalized.map(snapshotFile));
    if (stable.length === 1) {
      // Only one photo selected in the multi-flow — fall back to the
      // regular single-photo flow so the UX doesn't feel awkward.
      setMultiPhotoMode(false);
      setFileQueue([]);
      loadFileForCrop(stable[0]);
      return;
    }
    const capped = stable.slice(0, MAX_PHOTOS_PER_PLANT);
    if (stable.length > MAX_PHOTOS_PER_PLANT) {
      toast(`Using the first ${MAX_PHOTOS_PER_PLANT} photos (max per plant).`);
    }
    setMultiPhotoMode(true);
    setMultiCropQueue(capped);
    setMultiCropIndex(0);
    setMultiCroppedPhotos([]);
    setMultiCroppedHashes([]);
    // Reset batch state so the header/batch UI doesn't confuse the two flows.
    setFileQueue([]);
    setSavedPlants([]);
    setRequeuedFiles([]);
    setIsSecondPass(false);
    loadFileForCrop(capped[0]);
  };

  // If the modal is opened with a pre-supplied file (e.g. from a PWA
  // share-target intent), skip the upload step and drop straight into crop.
  useEffect(() => {
    if (initialFile) {
      setFileQueue([]);
      loadFileForCrop(initialFile);
    }
    // Only run on mount — a subsequent prop change would confusingly reset
    // the user's in-progress edits.
  }, []);

  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);

  const getCroppedImage = async () => {
    if (!completedCrop || !imageRef.current) return imagePreview;
    const image = imageRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const angle = (rotation * Math.PI) / 180;
    const sin = Math.abs(Math.sin(angle));
    const cos = Math.abs(Math.cos(angle));
    const cropWidth = completedCrop.width * scaleX;
    const cropHeight = completedCrop.height * scaleY;
    canvas.width = cropWidth * cos + cropHeight * sin;
    canvas.height = cropWidth * sin + cropHeight * cos;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(angle);
    ctx.translate(-cropWidth / 2, -cropHeight / 2);
    ctx.drawImage(image, completedCrop.x * scaleX, completedCrop.y * scaleY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
    return canvas.toDataURL('image/jpeg', 0.9);
  };

  // --- AI Identification ---

  const handleIdentify = async (extraCroppedPhotos = null, extraCroppedHashes = null) => {
    try {
      setIdentifying(true);
      const croppedImage = await getCroppedImage();
      setCroppedPreview(croppedImage);
      const base64Data = croppedImage.split(',')[1];
      // Compute hash of cropped image for duplicate detection
      const hash = await computeImageHash(base64Data);
      setCurrentImageHash(hash);

      // In multi-photo mode, the caller passes the already-cropped
      // previous photos so the FINAL identify call can include every
      // angle. We build `images_base64` from those + the current crop.
      // Legacy single-photo flow: `images_base64` stays null and the
      // server falls back to `image_base64`.
      const prevPhotos = Array.isArray(extraCroppedPhotos) ? extraCroppedPhotos : [];
      const isMultiIdentify = prevPhotos.length > 0;
      const imagesForAi = isMultiIdentify
        ? [...prevPhotos.map(dataUrl => dataUrl.split(',')[1]), base64Data]
        : null;

      // Commit the running photo list up front so a subsequent /identify
      // failure (network / 429 / AI error) doesn't silently discard the
      // photo we just cropped — `savePlant` reads `multiCroppedPhotos`
      // even after the flow bails out to the Details step.
      if (isMultiIdentify) {
        setMultiCroppedPhotos([...prevPhotos, croppedImage]);
        setMultiCroppedHashes([...(extraCroppedHashes || []), hash]);
      }

      let response;
      const requestBody = imagesForAi
        ? { images_base64: imagesForAi, mime_type: 'image/jpeg' }
        : { image_base64: base64Data, mime_type: 'image/jpeg' };
      if (isLocalMode) {
        response = await axios.post(
          `${BACKEND_URL}/api/plants/identify-anonymous`,
          requestBody
        );
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        response = await axios.post(
          `${BACKEND_URL}/api/plants/identify`,
          requestBody,
          { headers: { Authorization: `Bearer ${sessionToken}` } }
        );
      }
      setPlantData(normalizePlantData({
        common_name: response.data.common_name || '',
        scientific_name: response.data.scientific_name || '',
        care_details: response.data.care_details || {}
      }));
      setStep('identified');
      setIdentifyFailed(false);
      toast.success('Plant identified!');
    } catch (error) {
      console.error('Identification failed:', error);
      const croppedImage = await getCroppedImage();
      setCroppedPreview(croppedImage);
      toast.error('AI could not identify this plant. Please enter details manually.');
      setIdentifyFailed(true);
      setStep('details');
    } finally {
      setIdentifying(false);
    }
  };

  // Retry the AI identification without redoing any crops. Reuses the
  // cropped photos we already have on hand (either `multiCroppedPhotos`
  // for multi-mode or `croppedPreview` for single-mode) so the user can
  // recover from a transient AI/network failure with one tap from the
  // Details step. On success, jumps back to the IdentifiedStep so the
  // usual flow (Accept -> Details -> Save) resumes.
  const handleRetryIdentify = async () => {
    if (identifying) return;
    try {
      setIdentifying(true);
      let imagesForAi = null;
      let primaryBase64 = null;
      if (multiPhotoMode && multiCroppedPhotos.length > 0) {
        imagesForAi = multiCroppedPhotos.map(dataUrl => dataUrl.split(',')[1]);
        primaryBase64 = imagesForAi[0];
      } else if (croppedPreview) {
        primaryBase64 = croppedPreview.split(',')[1];
      } else {
        // Nothing to retry with — this shouldn't happen because the
        // button only appears after a failed identify (which sets
        // croppedPreview), but guard it anyway.
        toast.error('No photo to retry with. Please start again.');
        setIdentifying(false);
        return;
      }
      // Refresh the hash from the primary photo just in case (defence
      // in depth — normally already set).
      if (primaryBase64) {
        const hash = await computeImageHash(primaryBase64);
        setCurrentImageHash(hash);
      }

      const requestBody = imagesForAi
        ? { images_base64: imagesForAi, mime_type: 'image/jpeg' }
        : { image_base64: primaryBase64, mime_type: 'image/jpeg' };
      let response;
      if (isLocalMode) {
        response = await axios.post(
          `${BACKEND_URL}/api/plants/identify-anonymous`,
          requestBody
        );
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        response = await axios.post(
          `${BACKEND_URL}/api/plants/identify`,
          requestBody,
          { headers: { Authorization: `Bearer ${sessionToken}` } }
        );
      }
      setPlantData(normalizePlantData({
        common_name: response.data.common_name || '',
        scientific_name: response.data.scientific_name || '',
        care_details: response.data.care_details || {}
      }));
      setIdentifyFailed(false);
      setStep('identified');
      toast.success('Plant identified!');
    } catch (error) {
      console.error('Retry identify failed:', error);
      const detail = error?.response?.data?.detail || error?.message || 'AI could not identify this plant.';
      toast.error(detail);
      // Stay on the Details step so the user can keep entering things
      // manually — the retry button stays visible for another try.
    } finally {
      setIdentifying(false);
    }
  };

  const handleLookupByName = async (name) => {
    try {
      setLookingUp(true);
      let response;
      if (isLocalMode) {
        response = await axios.post(
          `${BACKEND_URL}/api/plants/care-details-anonymous`,
          { plant_name: name }
        );
      } else {
        const sessionToken = localStorage.getItem('auth_hint');
        response = await axios.post(
          `${BACKEND_URL}/api/plants/care-details`,
          { plant_name: name },
          { headers: { Authorization: `Bearer ${sessionToken}` } }
        );
      }
      setPlantData(normalizePlantData({
        common_name: response.data.common_name || name,
        scientific_name: response.data.scientific_name || '',
        care_details: response.data.care_details || {}
      }));
      setStep('details');
      setIdentifyFailed(false);
      toast.success('Care details filled in!');
    } catch (error) {
      toast.error('Could not look up care details. Please enter them manually.');
      setPlantData(normalizePlantData({
        common_name: name, scientific_name: '',
        care_details: { watering: '', light: '', humidity: '', temperature: '', toxicity: '', growth_rate: '' }
      }));
      setStep('details');
    } finally {
      setLookingUp(false);
    }
  };

  // --- Duplicate Detection (requires BOTH same name AND same photo) ---

  const findDuplicate = (name) => {
    if (!name?.trim() || !currentImageHash) return null;
    const normalised = name.trim().toLowerCase();
    return existingPlants.find(p =>
      p.plant_id !== editPlant?.plant_id &&
      p.common_name?.toLowerCase() === normalised &&
      p.image_hash === currentImageHash
    );
  };

  // Non-blocking same-PHOTO heads-up (hash match, name-agnostic). Surfaces a
  // banner on the Identified/Details steps so the user knows this exact photo
  // already exists — in their library OR earlier in this batch — and can add
  // it anyway or remove it. Distinct from the blocking name+photo dup dialog.
  const photoDupMatch = useMemo(() => {
    if (editPlant || !currentImageHash) return null;
    const lib = existingPlants.find(p => {
      const hashes = p.image_hashes || (p.image_hash ? [p.image_hash] : []);
      return hashes.includes(currentImageHash);
    });
    if (lib) return { where: 'library', name: lib.common_name || '' };
    if (batchHashes.includes(currentImageHash)) return { where: 'batch', name: '' };
    return null;
  }, [editPlant, currentImageHash, existingPlants, batchHashes]);

  // --- Save Logic ---

  const savePlant = async (incomplete = false, replaceId = null) => {
    const checkName = (plantData.common_name || '').trim();
    if (!incomplete && !checkName) {
      toast.error('Please enter a plant name');
      return false;
    }

    try {
      setSaving(true);
      const normalized = normalizePlantData(plantData);
      const saveName = normalized.common_name || (incomplete ? 'Unnamed Plant' : '');

      if (replaceId) {
        if (isLocalMode) {
          await localStore.deletePlant(replaceId);
        } else {
          const sessionToken = localStorage.getItem('auth_hint');
          await axios.delete(`${BACKEND_URL}/api/plants/${replaceId}`,
            { headers: { Authorization: `Bearer ${sessionToken}` } });
        }
      }

      let base64Data = null;
      if (croppedPreview) {
        base64Data = croppedPreview.split(',')[1];
      } else if (imagePreview && imagePreview !== 'existing') {
        const croppedImage = await getCroppedImage();
        if (croppedImage) base64Data = croppedImage.split(',')[1];
      }

      // Multi-photo mode collects every cropped photo in
      // `multiCroppedPhotos` (dataURLs). The FIRST photo becomes the
      // plant's cover; the rest are attached via `/api/plants/{id}/photos`
      // (same endpoint used by consolidation). Keeps the create path
      // simple and reuses proven photo-upload code paths.
      let extraPhotosBase64 = [];
      if (multiPhotoMode && !editPlant && multiCroppedPhotos.length > 0) {
        const firstDataUrl = multiCroppedPhotos[0];
        base64Data = firstDataUrl.split(',')[1];
        for (let i = 1; i < multiCroppedPhotos.length; i++) {
          extraPhotosBase64.push(multiCroppedPhotos[i].split(',')[1]);
        }
      }

      if (isLocalMode) {
        // Save to IndexedDB
        const plantRecord = {
          common_name: saveName,
          scientific_name: normalized.scientific_name || '',
          care_details: normalized.care_details,
          incomplete: incomplete,
          image_hash: currentImageHash || (editPlant?.image_hash ?? null),
          has_photo: base64Data ? true : (editPlant?.has_photo ?? false),
        };

        if (editPlant) {
          await localStore.updatePlant(editPlant.plant_id, plantRecord);
          if (base64Data) await localStore.savePhoto(editPlant.plant_id, base64Data);
          toast.success(incomplete ? 'Saved as incomplete' : 'Plant updated!');
          onPlantAdded();
          return true;
        }

        const saved = await localStore.addPlant(plantRecord);
        if (base64Data) await localStore.savePhoto(saved.plant_id, base64Data);
        // Attach any additional multi-photos (best-effort — one failure
        // shouldn't nuke the plant we just created).
        for (const extra of extraPhotosBase64) {
          try { await localStore.addPhotoToPlant(saved.plant_id, extra); }
          catch (err) { console.warn('Extra photo save failed (local):', err?.message || err); }
        }
        return saved.plant_id;
      }

      // Google Drive mode
      const sessionToken = localStorage.getItem('auth_hint');
      const payload = {
        common_name: saveName,
        scientific_name: normalized.scientific_name || '',
        image_base64: base64Data,
        care_details: normalized.care_details,
        incomplete: incomplete
      };

      if (editPlant) {
        const putResp = await axios.put(`${BACKEND_URL}/api/plants/${editPlant.plant_id}`, payload,
          { headers: { Authorization: `Bearer ${sessionToken}` } });
        if (!putResp?.data?.queued) toast.success(incomplete ? 'Saved as incomplete' : 'Plant updated!');
        onPlantAdded();
        return true;
      }

      const postResp = await axios.post(`${BACKEND_URL}/api/plants`, payload,
        { headers: { Authorization: `Bearer ${sessionToken}` } });
      const newPlantId = postResp?.data?.plant_id;

      // Multi-photo mode: attach the remaining photos to the freshly
      // created plant via the existing photo endpoint. Serial to keep
      // Drive-write ordering deterministic and avoid concurrent-quota
      // errors. Best-effort — a partial failure doesn't undo the plant.
      if (newPlantId && extraPhotosBase64.length > 0) {
        for (const extra of extraPhotosBase64) {
          try {
            await axios.post(
              `${BACKEND_URL}/api/plants/${newPlantId}/photos`,
              { image_base64: extra },
              { headers: { Authorization: `Bearer ${sessionToken}` } }
            );
          } catch (err) {
            console.warn('Extra photo upload failed:', err?.response?.data?.detail || err?.message || err);
          }
        }
      }

      // iter 56 — Auto-Backfill On Add. If the user opted in via Settings,
      // silently fire a single-plant backfill so any AI-omitted fields
      // (dormancy, environment, etc.) get topped up in the background.
      // Fire-and-forget: never block the add flow on it, never surface
      // errors — this is a nice-to-have. Rate-limited server-side.
      try {
        if (newPlantId) {
          const settingsResp = await axios.get(`${BACKEND_URL}/api/settings`,
            { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 5000 });
          // Treat undefined/null as opt-in: users who haven't explicitly
          // turned this off get the "fill everything on add" experience by
          // default, so every new plant is topped up without them having
          // to remember the Settings toggle.
          if (settingsResp?.data?.auto_backfill_on_add !== false) {
            axios.post(
              `${BACKEND_URL}/api/plants/backfill-missing-care-details?limit=1&plant_id=${encodeURIComponent(newPlantId)}`,
              {},
              { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 90000 }
            ).catch((err) => { console.debug('Auto-backfill on add skipped:', err?.message); });
          }
        }
      } catch (_e) { /* silent — never block the happy add path */ }
      return newPlantId || true;
    } catch (error) {
      // Offline block — the axios interceptor already showed the
      // friendly "You're offline — reconnect to save changes." toast,
      // so don't stack a raw internal error on top.
      if (error?.code === 'ERR_OFFLINE') return false;
      console.error('Failed to save plant:', error);
      const msg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to save plant: ${msg}`);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // --- Queue & Batch Management ---

  const advanceQueue = (plantEntry) => {
    const newSaved = [...savedPlants, plantEntry];
    setSavedPlants(newSaved);
    // Remember this photo's hash so the next photos in the batch can be
    // flagged if they duplicate it.
    if (currentImageHash) setBatchHashes(prev => prev.includes(currentImageHash) ? prev : [...prev, currentImageHash]);

    const nextIndex = currentQueueIndex + 1;
    if (nextIndex < fileQueue.length) {
      setCurrentQueueIndex(nextIndex);
      loadFileForCrop(fileQueue[nextIndex]);
    } else if (!isSecondPass && requeuedFiles.length > 0) {
      setFileQueue(requeuedFiles);
      setRequeuedFiles([]);
      setCurrentQueueIndex(0);
      setIsSecondPass(true);
      toast(t('returningToSkipped'));
      loadFileForCrop(requeuedFiles[0]);
    } else {
      setStep('summary');
    }
  };

  const getQueueStatus = () => {
    const remaining = fileQueue.length - currentQueueIndex - 1 + requeuedFiles.length;
    if (remaining > 0) return `${remaining} photo${remaining !== 1 ? 's' : ''} remaining`;
    return '';
  };

  // --- Save Orchestration ---

  const trySave = (incomplete = false) => {
    // First check exact duplicate (same name AND same photo)
    const dup = findDuplicate(plantData.common_name);
    if (dup && !editPlant) {
      setDuplicateMatch(dup);
      setPendingSaveIncomplete(incomplete);
      return;
    }
    // Then check name-only match (same name, different photo) → offer consolidation
    if (!editPlant) {
      const nameMatch = findNameOnlyMatch(plantData.common_name, currentImageHash, existingPlants);
      if (nameMatch) {
        setConsolidateMatch(nameMatch);
        setConsolidatePendingIncomplete(incomplete);
        return;
      }
    }
    incomplete ? doCompleteLater() : doSave();
  };

  const advanceAfterConsolidateFail = () => {
    const nextIndex = currentQueueIndex + 1;
    if (nextIndex < fileQueue.length) {
      setCurrentQueueIndex(nextIndex);
      loadFileForCrop(fileQueue[nextIndex]);
    } else if (savedPlants.length > 0) {
      setStep('summary');
    }
  };

  // Shared uploader for merge/consolidation — used by the initial attempt and
  // the retry path. Throws on failure so callers can decide retry vs advance.
  const consolidateUpload = async (target, base64Data) => {
    if (isLocalMode) {
      await localStore.addPhotoToPlant(target.plant_id, base64Data);
    } else {
      await withRetry(async () => {
        const sessionToken = localStorage.getItem('auth_hint');
        return axios.post(`${BACKEND_URL}/api/plants/${target.plant_id}/photos`, {
          image_base64: base64Data,
        }, { headers: { Authorization: `Bearer ${sessionToken}` } });
      });
    }
    const entry = { common_name: target.common_name, consolidated: true, plant_id: target.plant_id };
    if (batchMode) {
      advanceQueue(entry);
    } else {
      onPlantAdded();
    }
    toast.success(`Photo added to "${target.common_name}"!`);
  };

  const handleConsolidate = async () => {
    const target = consolidateMatch;
    setConsolidateMatch(null);
    if (!target) return;

    let base64Data = null;
    if (croppedPreview) {
      base64Data = croppedPreview.split(',')[1];
    } else if (imagePreview && imagePreview !== 'existing') {
      const croppedImage = await getCroppedImage();
      if (croppedImage) base64Data = croppedImage.split(',')[1];
    }
    if (!base64Data) {
      toast.error('No photo to add');
      return;
    }

    setSaving(true);
    try {
      await consolidateUpload(target, base64Data);
    } catch (error) {
      if (error?.__queued) {
        // Offline: queued for sync (interceptor toasted). Move on.
        if (batchMode) advanceAfterConsolidateFail(); else onPlantAdded();
        return;
      }
      const status = error?.response?.status;
      const msg = error?.response?.data?.detail || error?.message || 'Failed to add photo';
      toast.error(msg);
      // Permanent rejection (409 dup / 400 max) → skip on in batch. Transient →
      // keep the photo and offer a retry via the dialog.
      if (status === 400 || status === 409) {
        if (batchMode) advanceAfterConsolidateFail();
      } else {
        setFailedConsolidate({ target, base64: base64Data });
      }
    } finally {
      setSaving(false);
    }
  };

  const retryConsolidate = async () => {
    const fc = failedConsolidate;
    setFailedConsolidate(null);
    if (!fc) return;
    setSaving(true);
    try {
      await consolidateUpload(fc.target, fc.base64);
    } catch (error) {
      if (error?.__queued) {
        if (batchMode) advanceAfterConsolidateFail(); else onPlantAdded();
        return;
      }
      const status = error?.response?.status;
      const msg = error?.response?.data?.detail || error?.message || 'Failed to add photo';
      toast.error(msg);
      if (status === 400 || status === 409) {
        if (batchMode) advanceAfterConsolidateFail();
      } else {
        setFailedConsolidate(fc);
      }
    } finally {
      setSaving(false);
    }
  };

  const skipFailedConsolidate = () => {
    setFailedConsolidate(null);
    if (batchMode) advanceAfterConsolidateFail();
  };

  const handleConsolidateCreateNew = () => {
    setConsolidateMatch(null);
    consolidatePendingIncomplete ? doCompleteLater() : doSave();
  };

  const doSave = async (replaceId = null) => {
    setDuplicateMatch(null);
    const success = await savePlant(false, replaceId);
    if (!success) {
      if (batchMode) {
        toast.error('Save failed — skipping to next photo');
        const nextIndex = currentQueueIndex + 1;
        if (nextIndex < fileQueue.length) {
          setCurrentQueueIndex(nextIndex);
          loadFileForCrop(fileQueue[nextIndex]);
        } else if (savedPlants.length > 0) {
          setStep('summary');
        }
      }
      return;
    }

    const entry = { common_name: plantData.common_name, scientific_name: plantData.scientific_name, plant_id: typeof success === 'string' ? success : null };
    if (batchMode) {
      advanceQueue(entry);
      toast.success(`Saved! ${getQueueStatus()}`);
    } else {
      // savePlant already toasts "Plant updated!" on the edit path, so
      // only announce a fresh add here.
      if (!editPlant) toast.success('Plant added successfully!');
      onPlantAdded();
    }
  };

  const doCompleteLater = async (replaceId = null) => {
    setDuplicateMatch(null);
    const success = await savePlant(true, replaceId);
    if (!success) {
      if (batchMode) {
        toast.error('Save failed — skipping to next photo');
        const nextIndex = currentQueueIndex + 1;
        if (nextIndex < fileQueue.length) {
          setCurrentQueueIndex(nextIndex);
          loadFileForCrop(fileQueue[nextIndex]);
        } else if (savedPlants.length > 0) {
          setStep('summary');
        }
      }
      return;
    }

    const entry = { common_name: plantData.common_name || 'Unnamed Plant', incomplete: true, plant_id: typeof success === 'string' ? success : null };
    if (batchMode) {
      advanceQueue(entry);
      toast(t('savedAsIncompleteNext'));
    } else {
      toast(t('savedAsIncompleteLater'));
      onPlantAdded();
    }
  };

  const handleSave = () => trySave(false);
  const handleCompleteLater = () => trySave(true);

  // --- Duplicate Resolution ---

  const handleDuplicateKeepNew = () => {
    if (pendingSaveIncomplete) doCompleteLater(duplicateMatch.plant_id);
    else doSave(duplicateMatch.plant_id);
  };

  const handleDuplicateKeepExisting = () => {
    setDuplicateMatch(null);
    if (batchMode) {
      const nextIndex = currentQueueIndex + 1;
      if (nextIndex < fileQueue.length) {
        setCurrentQueueIndex(nextIndex);
        loadFileForCrop(fileQueue[nextIndex]);
        toast(t('keptExistingNext'));
      } else if (savedPlants.length > 0) {
        setStep('summary');
      } else {
        clearBatchDraft();
        onClose();
      }
    } else {
      toast(t('keptExistingPlant'));
      onPlantAdded();
    }
  };

  const handleDuplicateKeepBoth = () => {
    if (pendingSaveIncomplete) doCompleteLater();
    else doSave();
  };

  // --- Skip & Crop Handlers ---

  const handleSkipPhoto = () => {
    if (!batchMode) return;

    if (isSecondPass) {
      handleCompleteLater();
      return;
    }

    const currentFile = fileQueue[currentQueueIndex];
    setRequeuedFiles(prev => [...prev, currentFile]);

    const nextIndex = currentQueueIndex + 1;
    if (nextIndex < fileQueue.length) {
      setCurrentQueueIndex(nextIndex);
      loadFileForCrop(fileQueue[nextIndex]);
      toast(t('skippedWillReturn'));
    } else if (requeuedFiles.length > 0 || currentFile) {
      const allRequeued = [...requeuedFiles, currentFile];
      setFileQueue(allRequeued);
      setRequeuedFiles([]);
      setCurrentQueueIndex(0);
      setIsSecondPass(true);
      toast(t('returningToSkipped'));
      loadFileForCrop(allRequeued[0]);
    } else {
      if (savedPlants.length > 0) { setStep('summary'); } else { clearBatchDraft(); onClose(); }
    }
  };

  // Permanently DROP the current photo from the batch (distinct from Skip,
  // which re-queues it for a second pass). Nothing is saved for this photo;
  // the rest of the queue — including any already-skipped photos — is kept.
  const confirmRemovePhoto = () => {
    setShowRemoveConfirm(false);
    const remaining = fileQueue.length - currentQueueIndex - 1 + requeuedFiles.length;

    // Snapshot everything needed to fully restore this photo on Undo. Remove
    // never mutates fileQueue in the simple case (we just advance the index),
    // but the second-pass switch below does — so capture the queues too.
    undoRemoveRef.current = {
      file: fileQueue[currentQueueIndex],
      fileQueue, currentQueueIndex, requeuedFiles, isSecondPass,
      hadSaved: savedPlants.length > 0,
    };

    const nextIndex = currentQueueIndex + 1;
    let stayedOpen = true;
    if (nextIndex < fileQueue.length) {
      setCurrentQueueIndex(nextIndex);
      loadFileForCrop(fileQueue[nextIndex]);
    } else if (!isSecondPass && requeuedFiles.length > 0) {
      setFileQueue(requeuedFiles);
      setRequeuedFiles([]);
      setCurrentQueueIndex(0);
      setIsSecondPass(true);
      loadFileForCrop(requeuedFiles[0]);
    } else if (savedPlants.length > 0) {
      setStep('summary');
    } else {
      stayedOpen = false;
      clearBatchDraft();
      onClose();
    }

    // Inline undo notice (only while the modal stays open — otherwise there's
    // nothing to attach it to). Auto-dismisses after a few seconds.
    if (stayedOpen) {
      if (removedTimerRef.current) clearTimeout(removedTimerRef.current);
      setRemovedNotice({ count: remaining });
      removedTimerRef.current = setTimeout(() => setRemovedNotice(null), 8000);
    }
  };

  // Restore the just-removed photo back into the batch at its original slot.
  const undoRemovePhoto = () => {
    const snap = undoRemoveRef.current;
    if (!snap) return;
    undoRemoveRef.current = null;
    if (removedTimerRef.current) clearTimeout(removedTimerRef.current);
    setRemovedNotice(null);
    setFileQueue(snap.fileQueue);
    setRequeuedFiles(snap.requeuedFiles);
    setIsSecondPass(snap.isSecondPass);
    setCurrentQueueIndex(snap.currentQueueIndex);
    loadFileForCrop(snap.file);
  };

  useEffect(() => () => { if (removedTimerRef.current) clearTimeout(removedTimerRef.current); }, []);

  const handleDoneCropping = async () => {
    const cropped = await getCroppedImage();
    setCroppedPreview(cropped);

    // Multi-photo mode: stash the just-cropped photo and either move to
    // the next photo's crop screen OR (last one) call identify with the
    // full set. The AI sees all photos of the same plant together.
    if (multiPhotoMode) {
      const base64Data = cropped.split(',')[1];
      const hash = await computeImageHash(base64Data);
      const nextIndex = multiCropIndex + 1;

      if (nextIndex < multiCropQueue.length) {
        // More photos to crop — save this one to the running list and
        // advance. Identification runs on the LAST photo's Done tap so
        // we send one combined /identify call rather than N wasteful ones.
        setMultiCroppedPhotos((prev) => [...prev, cropped]);
        setMultiCroppedHashes((prev) => [...prev, hash]);
        setMultiCropIndex(nextIndex);
        loadFileForCrop(multiCropQueue[nextIndex]);
        return;
      }
      // Last photo — run the multi-image identify with every cropped
      // photo we've collected so far (plus the current one, which
      // handleIdentify appends internally).
      handleIdentify(multiCroppedPhotos, multiCroppedHashes);
      return;
    }

    handleIdentify();
  };

  const handleCropCancel = () => {
    // Cancelling out of the crop step from multi-photo mode fully aborts
    // the multi flow so the user returns to a clean Upload screen.
    if (multiPhotoMode) {
      setMultiPhotoMode(false);
      setMultiCropQueue([]);
      setMultiCropIndex(0);
      setMultiCroppedPhotos([]);
      setMultiCroppedHashes([]);
    }
    setStep('upload');
    setImagePreview(null);
    setSelectedFile(null);
    setRotation(0);
  };

  // --- Dialog Title ---

  const getTitle = () => {
    if (editPlant) return t('editPlant');
    if (step === 'upload') return t('addNewPlantTitle');
    if (step === 'summary') return t('allDone');
    if (step === 'crop') return t('cropAndRotate');
    if (step === 'identified') return t('aiIdentificationTitle');
    return t('plantDetailsTitle');
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl lg:max-w-4xl max-h-[90vh] overflow-y-auto"
        style={{
          resize: 'both', minWidth: '340px', minHeight: '280px', overflow: 'auto',
          transform: `translate(calc(-50% + ${dragOffset.x}px), calc(-50% + ${dragOffset.y}px))`
        }}
        aria-describedby={undefined}
        data-testid="add-plant-modal"
      >
        <DialogHeader
          onPointerDown={handleDragStart}
          style={{ cursor: 'grab', userSelect: 'none', touchAction: 'none' }}
        >
          <div className="flex items-center gap-2">
            <GripHorizontal className="w-4 h-4 flex-shrink-0" style={{ color: 'hsl(125, 12%, 58%)' }} />
            <DialogTitle className="text-2xl font-display" style={{ color: 'hsl(143, 28%, 25%)' }}>
              {getTitle()}
            </DialogTitle>
          </div>
          {batchMode && step !== 'summary' && step !== 'upload' && (
            <BatchProgress fileQueue={fileQueue} currentQueueIndex={currentQueueIndex} isSecondPass={isSecondPass} />
          )}
          {multiPhotoMode && step === 'crop' && multiCropQueue.length > 1 && (
            <div
              className="text-xs mt-1 font-medium"
              style={{ color: 'hsl(143, 28%, 25%)' }}
              data-testid="multi-photo-progress"
            >
              {(t('multiPhotoProgress') || 'Photo {n} of {total} — crop each one, then AI identifies from all')
                .replace('{n}', multiCropIndex + 1)
                .replace('{total}', multiCropQueue.length)}
            </div>
          )}
        </DialogHeader>

        {removedNotice && (
          <div
            className="mx-4 mt-1 mb-2 rounded-lg px-3 py-2 text-sm flex items-center gap-2"
            style={{ backgroundColor: 'hsl(143, 30%, 95%)', border: '1px solid hsl(143, 28%, 82%)', color: 'hsl(143, 28%, 25%)' }}
            role="status"
            aria-live="polite"
            data-testid="photo-removed-notice"
          >
            <span className="flex-1 min-w-0">
              {removedNotice.count > 0
                ? (t('photoRemovedWithCount') || 'Photo removed — {n} left').replace('{n}', removedNotice.count)
                : (t('photoRemoved') || 'Photo removed')}
            </span>
            <button
              type="button"
              onClick={undoRemovePhoto}
              className="flex-shrink-0 inline-flex items-center gap-1 font-semibold underline"
              style={{ color: 'hsl(143, 45%, 30%)' }}
              data-testid="undo-remove-photo-button"
            >
              <Undo2 className="w-3.5 h-3.5" />
              {t('undo') || 'Undo'}
            </button>
          </div>
        )}

        {!isLocalMode && !editPlant && isOffline && (
          <div
            className="mx-4 mt-1 mb-2 rounded-lg px-3 py-2 text-xs flex items-start gap-2"
            style={{ backgroundColor: 'hsl(40, 90%, 94%)', color: 'hsl(35, 60%, 28%)' }}
            data-testid="add-offline-notice"
          >
            <WifiOff className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span>
              {t('addOfflineNotice') || 'You\u2019re offline — adding a new plant needs a connection. You can still edit, archive and delete offline; those sync when you reconnect.'}
            </span>
          </div>
        )}

        {!editPlant && step === 'upload' && (
          <UploadStep
            cameraInputRef={cameraInputRef}
            fileInputRef={fileInputRef}
            handleFileSelect={handleFileSelect}
            handleMultiFileSelect={handleMultiFileSelect}
            onManualEntry={() => setStep('details')}
          />
        )}

        {step === 'crop' && imagePreview && (
          <CropStep
            imagePreview={imagePreview} identifying={identifying}
            crop={crop} setCrop={setCrop} setCompletedCrop={setCompletedCrop}
            rotation={rotation} imageRef={imageRef}
            batchMode={batchMode} handleSkipPhoto={handleSkipPhoto}
            onRemovePhoto={() => setShowRemoveConfirm(true)}
            onCancel={handleCropCancel}
            handleRotate={handleRotate} handleDoneCropping={handleDoneCropping}
          />
        )}

        {step === 'crop' && cropLoadError && !imagePreview && (
          <div className="text-center py-6" data-testid="photo-read-error">
            <p className="text-sm mb-4" style={{ color: 'hsl(0, 60%, 40%)' }}>
              {t('photoReadFailed') || "Couldn't read that photo — please pick a different one"}
            </p>
            <input
              ref={retryInputRef}
              type="file"
              accept="image/*"
              onChange={handleRetryPhoto}
              className="hidden"
              data-testid="retry-photo-input"
            />
            <div className="flex flex-col gap-2 items-center">
              <button
                onClick={() => retryInputRef.current?.click()}
                className="px-4 py-2 rounded-full text-sm font-medium"
                style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
                data-testid="retry-photo-button"
              >
                {t('chooseDifferentPhoto') || 'Choose a different photo'}
              </button>
              {batchMode && (
                <button
                  onClick={handleSkipPhoto}
                  className="text-sm underline"
                  style={{ color: 'hsl(125, 12%, 40%)' }}
                  data-testid="skip-failed-photo-button"
                >
                  {t('skipThisPhoto') || 'Skip this one'}
                </button>
              )}
            </div>
          </div>
        )}

        {step === 'identified' && (
          <IdentifiedStep
            croppedPreview={croppedPreview} plantData={plantData}
            editedName={editedName} setEditedName={setEditedName}
            onRecrop={() => setStep('crop')} onAccept={() => setStep('details')}
            handleCompleteLater={handleCompleteLater} saving={saving}
            batchMode={batchMode}
            onRemovePhoto={() => setShowRemoveConfirm(true)}
            photoDupMatch={photoDupMatch}
            lookingUp={lookingUp} handleLookupByName={handleLookupByName}
          />
        )}

        {step === 'details' && (
          <DetailsStep
            plantData={plantData} setPlantData={setPlantData}
            onClose={onClose} handleSave={handleSave}
            handleCompleteLater={handleCompleteLater} saving={saving}
            batchMode={batchMode}
            onRemovePhoto={() => setShowRemoveConfirm(true)}
            photoDupMatch={photoDupMatch}
            editPlant={editPlant} onDelete={onDelete}
            onAiLookup={(name) => {
              if (!name?.trim()) { toast.error(t('pleaseEnterName')); return; }
              handleLookupByName(name.trim());
            }}
            lookingUp={lookingUp}
            identifyFailed={identifyFailed && !editPlant}
            onRetryIdentify={handleRetryIdentify}
            identifying={identifying}
          />
        )}

        {step === 'summary' && (
          <SummaryStep savedPlants={savedPlants} onPlantAdded={onPlantAdded} onEditPlant={onEditPlant} />
        )}

        {duplicateMatch && (
          <DuplicateDialog
            duplicateMatch={duplicateMatch}
            handleDuplicateKeepNew={handleDuplicateKeepNew}
            handleDuplicateKeepBoth={handleDuplicateKeepBoth}
            handleDuplicateKeepExisting={handleDuplicateKeepExisting}
            saving={saving}
          />
        )}

        {consolidateMatch && (
          <ConsolidateDialog
            isOpen={!!consolidateMatch}
            existingPlant={consolidateMatch}
            onConsolidate={handleConsolidate}
            onCreateNew={handleConsolidateCreateNew}
            onCancel={() => setConsolidateMatch(null)}
          />
        )}

        <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
          <AlertDialogContent style={{ backgroundColor: 'white' }} data-testid="remove-photo-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle style={{ color: 'hsl(143, 28%, 25%)', fontSize: '1.5rem' }}>
                {t('removePhotoTitle') || 'Remove this photo?'}
              </AlertDialogTitle>
              <AlertDialogDescription style={{ color: 'hsl(125, 12%, 25%)', fontSize: '1rem' }}>
                {t('removePhotoDesc') || "This photo will be discarded and won't be added. The other photos in this batch stay in the queue."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
                data-testid="remove-photo-cancel-button"
              >
                {t('cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmRemovePhoto}
                style={{ backgroundColor: 'hsl(0, 70%, 50%)', color: 'white' }}
                data-testid="remove-photo-confirm-button"
              >
                {t('removePhotoConfirm') || 'Remove photo'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!failedConsolidate} onOpenChange={(o) => { if (!o) skipFailedConsolidate(); }}>
          <AlertDialogContent style={{ backgroundColor: 'white' }} data-testid="merge-retry-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle style={{ color: 'hsl(143, 28%, 25%)', fontSize: '1.5rem' }}>
                {t('mergeFailedTitle') || "Couldn't add the photo"}
              </AlertDialogTitle>
              <AlertDialogDescription style={{ color: 'hsl(125, 12%, 25%)', fontSize: '1rem' }}>
                {(t('mergeFailedDesc') || 'Something went wrong adding this photo to "{name}". Your photo is safe — you can try again.').replace('{name}', failedConsolidate?.target?.common_name || '')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={skipFailedConsolidate}
                style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
                data-testid="merge-retry-skip-button"
              >
                {batchMode ? (t('skip') || 'Skip') : (t('cancel') || 'Cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={retryConsolidate}
                disabled={saving}
                style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
                data-testid="merge-retry-button"
              >
                {t('retry') || 'Retry'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};
